import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  FaFilePdf, FaFileExcel, FaFileCsv, FaDownload,
  FaIndustry, FaChartBar, FaCheckCircle,
  FaClock, FaCertificate,
  FaSearch, FaFilter, FaChevronDown, FaChevronUp,
  FaUser, FaCalendarAlt, FaTag, FaQuestionCircle, FaExclamationCircle,
  FaRobot, FaFileAlt, FaExpandArrowsAlt, FaLock,
  FaFile, FaLeaf
} from 'react-icons/fa';
import { Spinner } from 'react-bootstrap';
import {
  pdfEscapeText,
  groupSection91Rows,
  section91RowHasAnswer,
  buildTopicCompletedRequirements,
  formatValueUnitsDisplay,
  formatFYReportingPeriod,
  formatFillInBlanksAnswerDisplay,
  hasSubstantiveSection91Answer,
} from './section91ReportUtils';

// Mock data based on the provided images and logic
const mockReportData = {
  clientName: "MINERA VERDE S.A.",
  sasbSector: "Extractives and Minerals Processing",
  sasbIndustry: "Metals and Mining",
  reportingPeriod: "Fiscal Year 2023",
  reportDate: "March 15, 2024",
  finalizedYear: "2023",

  disclosureFrameworkSummary: [
    { framework: "CMF NCG 461", numberOfDisclosures: 9, statusPercent: 42, show: true },
    { framework: "IFRS S1/S2", numberOfDisclosures: 12, statusPercent: 19, show: true },
  ],

  section91Disclosures: [],
  detailedDisclosures: [
    {
      dimensionName: "Social Capital",
      definition: "Social capital encompasses our relationships with communities, customers, and other stakeholders. We recognize the importance of maintaining strong connections with these groups to ensure our long-term success.",
      disclosureTopics: [
        {
          ifrsDisclosureTopic: "Human Rights & Community Relations",
          completedRequirements: {
            ifrsSasb: "IFRS S1/S2",
            sustainabilityDimension: "Social Capital",
            disclosureTopic: "Social Capital",
            disclosureFieldTitle: "Human Rights & Community Relations",
            sasbAuditCode: "GRI 410, GRI 411",
            cmfRequirements: "Disclosure of human rights policies and alignment with UN guidelines (NCG 461: Article 8.1.2)",
            ncg30461Article: "8.1.2",
            ncg30519Article: "—",
          },
          questionsAndAnswers: [
            {
              question: "Does your company have a formal Human Rights Policy that applies to employees, suppliers and community stakeholders?",
              answer: "Yes, Minera Verde has a comprehensive Human Rights Policy that explicitly applies to all employees, contractors, suppliers, and community stakeholders. The policy was established in 2015 and underwent a significant update in 2022 to align with the UN Guiding Principles on Business and Human Rights. The policy includes specific provisions for indigenous peoples' rights, cultural heritage protection, and grievance mechanisms.",
            },
          ]
        }
      ]
    },
  ],
};

// Format emissions category for display (e.g. grid_electricity -> "Grid electricity")
const formatEmissionsCategory = (category) => {
  if (!category) return '';
  return category
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

// This will be calculated dynamically from reportData state

const ReportsPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [exporting, setExporting] = useState(false);
  const [generatingReport, setGeneratingReport] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    compliance: true,
    context: true,
    emissions: true,
    section91: true,
    detailed: true,
    assurance: true,
    supportingDocuments: true,
  });
  const [searchTerm, setSearchTerm] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  
  // Get filter values from URL params (passed from Reporting page)
  const searchParams = new URLSearchParams(location.search);
  const [selectedCustomer, setSelectedCustomer] = useState(searchParams.get('customer') || '');
  const [selectedTopic, setSelectedTopic] = useState(searchParams.get('topic') || '');
  const [selectedField, setSelectedField] = useState(searchParams.get('field') || '');
  const [companyAdmins, setCompanyAdmins] = useState([]);

  // Dynamic data states
  const [loading, setLoading] = useState(true);
  const [reports, setReports] = useState([]);
  const [summary, setSummary] = useState({});
  const [reportData, setReportData] = useState(mockReportData);
  const [emissionsSummary, setEmissionsSummary] = useState([]);

  // Update state when URL parameters change
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const customerParam = params.get('customer') || '';
    const topicParam = params.get('topic') || '';
    const fieldParam = params.get('field') || '';
    
    setSelectedCustomer(customerParam);
    setSelectedTopic(topicParam);
    setSelectedField(fieldParam);
  }, [location.search]);

  // Fetch company admins if user is superAdmin
  // For Company Admin, automatically set their own ID
  useEffect(() => {
    const userRole = localStorage.getItem('role');
    if (userRole === 'superAdmin') {
      fetchCompanyAdmins();
    } else if (userRole === 'companyAdmin') {
      // Company Admin: Automatically set their own ID as selected customer (default)
      const currentUserId = localStorage.getItem('userId');
      if (currentUserId && !selectedCustomer) {
        setSelectedCustomer(currentUserId);
      }
    }
  }, []);

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchAllData();
  }, [selectedCustomer, selectedTopic, selectedField]);

  const fetchCompanyAdmins = async () => {
    try {
      const response = await axios.get("company-admins");
      if (response.data.status) {
        setCompanyAdmins(response.data.companyAdmins || []);
      }
    } catch (error) {
      console.error("Error fetching company admins:", error);
    }
  };

  const fetchAllData = async () => {
    const userRole = localStorage.getItem('role');
    
    // For superAdmin, don't fetch data if no customer is selected
    if (userRole === 'superAdmin' && !selectedCustomer) {
      setLoading(false);
      setReports([]);
      setSummary({});
      setReportData(mockReportData);
      setEmissionsSummary([]);
      return;
    }
    
    setLoading(true);
    try {
      // Build query parameters with current filters
      const summaryParams = new URLSearchParams();
      const reportsParams = new URLSearchParams();
      
      // Add customer filter for Super Admin or Company Admin (their own company)
      if (selectedCustomer) {
        summaryParams.append("userId", selectedCustomer);
        reportsParams.append("userId", selectedCustomer);
      } else if (userRole === 'companyAdmin') {
        // Company Admin: Always filter by their own ID
        const currentUserId = localStorage.getItem('userId');
        if (currentUserId) {
          summaryParams.append("userId", currentUserId);
          reportsParams.append("userId", currentUserId);
        }
      }
      
      if (selectedTopic) {
        reportsParams.append("topic", selectedTopic);
      }
      if (selectedField) {
        reportsParams.append("field", selectedField);
      }

      // Target user for this report (customer dropdown or current user for company admin)
      const targetUserId = selectedCustomer || (userRole === 'companyAdmin' ? localStorage.getItem('userId') : null);

      // Fetch report data and emissions data in parallel when we have a target user
      const [summaryResponse, reportsResponse, emissionsResponse] = await Promise.all([
        axios.get(`summary?${summaryParams.toString()}`),
        axios.get(`reports?${reportsParams.toString()}`),
        targetUserId
          ? axios.get('emissions-questions/answers-by-user', { params: { userId: targetUserId, customer: targetUserId } })
          : Promise.resolve({ data: { status: true, emissionsSummary: [] } })
      ]);

      let summaryData = {};
      if (summaryResponse.data.status) {
        summaryData = summaryResponse.data.summary || {};
        setSummary(summaryData);
      }

      if (reportsResponse.data.status) {
        const reportsData = reportsResponse.data.reports || [];
        setReports(reportsData);
        transformDataForReport(reportsData);
      }

      if (emissionsResponse.data.status && emissionsResponse.data.emissionsSummary) {
        setEmissionsSummary(emissionsResponse.data.emissionsSummary);
      } else {
        setEmissionsSummary([]);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
      toastr.error(t('reporting.failedToLoadReportData'));
    } finally {
      setLoading(false);
    }
  };

  // Transform API data to match the report structure
  const transformDataForReport = (apiReports) => {
    if (!apiReports || apiReports.length <= 1) return;

    const reportsBody = apiReports.slice(1);

    const detailedDisclosures = [];
    const dimensionsMap = new Map();

    const section91Disclosures = [];

    reportsBody.forEach(report => {
      (report.questions || []).forEach(question => {
        if (question.section9_1) {
          const answerText = (question.answer != null ? String(question.answer) : '').trim();
          const hasAnswer = hasSubstantiveSection91Answer(answerText);
          const reasonTrim =
            question.noAnswerReason != null && String(question.noAnswerReason).trim() !== ''
              ? String(question.noAnswerReason).trim()
              : '';
          const units = formatValueUnitsDisplay(question.valueUnitFormat);
          const periodLabel = formatFYReportingPeriod(
            question.reportingPeriodStart,
            question.reportingPeriodEnd,
            i18n.language
          );
          const rawDimension = question.dimension || question.sector || 'General';
          const dimensionKey = rawDimension === 'Environmental' ? 'Environment' : rawDimension;
          section91Disclosures.push({
            rowKey: `${report.assignmentId ?? 'na'}_${question.id}`,
            dimensionName: dimensionKey,
            disclosureTopic: report.disclosureTopic,
            disclosureField: report.disclosureField,
            standards: report.standards,
            engagementQuestion: question.engagementQuestion || '—',
            completedRequirements: buildTopicCompletedRequirements(report, question, dimensionKey),
            questionIdentifier: question.questionIdentifier || '—',
            sasbMetricName: question.sasbMetricName || '—',
            answer: answerText || '—',
            valueUnits: units || '—',
            reportingPeriod: periodLabel || '—',
            noAnswerReason: !hasAnswer && reasonTrim ? reasonTrim : '—',
          });
          return;
        }

        const rawDimension = question.dimension || question.sector || "General";
        const dimensionKey = rawDimension === "Environmental" ? "Environment" : rawDimension;
        if (!dimensionsMap.has(dimensionKey)) {
          dimensionsMap.set(dimensionKey, {
            dimensionName: dimensionKey,
            definition: `${dimensionKey} reflects disclosures grouped under this IFRS/SASB sustainability dimension in EcoMina.`,
            disclosureTopics: new Map()
          });
        }

        const dimension = dimensionsMap.get(dimensionKey);
        const topicKey = [report.standards, report.disclosureTopic, report.disclosureField].join('|');

        if (!dimension.disclosureTopics.has(topicKey)) {
          dimension.disclosureTopics.set(topicKey, {
            topicKey,
            ifrsDisclosureTopic: report.disclosureField || report.disclosureTopic,
            completedRequirements: null,
            questionsAndAnswers: []
          });
        }

        const topicEntry = dimension.disclosureTopics.get(topicKey);

        if (question.answer && String(question.answer).trim() !== '') {
          if (!topicEntry.completedRequirements) {
            topicEntry.completedRequirements = buildTopicCompletedRequirements(report, question, dimensionKey);
          }
          topicEntry.questionsAndAnswers.push({
            question: question.engagementQuestion,
            answer: question.answer,
          });
        }
      });
    });

    // Convert maps to arrays
    const detailedDisclosuresArray = Array.from(dimensionsMap.values()).map(dimension => ({
      ...dimension,
      disclosureTopics: Array.from(dimension.disclosureTopics.values())
    }));

    // Get company info and KYC data from the first report (which contains companyInfo)
    const companyInfo = apiReports[0]?.companyInfo || {};
    const kycData = apiReports[0]?.kycData || {};

    // Update report data
    setReportData({
      clientName: companyInfo.companyName || t('reporting.companyName'),
      sasbSector: kycData.sasbSector || t('reporting.generalSector'),
      sasbIndustry: kycData.sasbIndustry || t('reporting.generalIndustry'),
      reportingPeriod: `${t('reporting.fiscalYear')} ${new Date().getFullYear()}`,
      reportDate: new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }),
      finalizedYear: new Date().getFullYear().toString(),
      disclosureFrameworkSummary: [
        { framework: "CMF NCG 461", numberOfDisclosures: 9, statusPercent: 42, show: true },
        { framework: "IFRS S1/S2", numberOfDisclosures: 12, statusPercent: 19, show: true }
      ],
      detailedDisclosures: detailedDisclosuresArray.filter(d => d.disclosureTopics.length > 0),
      section91Disclosures,
    });
  };

  const emissionsRowsForTotal = useMemo(
    () =>
      emissionsSummary.filter(
        (item) => !['batch', 'organization_context'].includes(item.questionSet?.category)
      ),
    [emissionsSummary]
  );

  const totalEmissionsTcO2e = useMemo(
    () =>
      emissionsRowsForTotal.reduce((sum, item) => {
        const v = item.answer?.answers?.calculation?.co2e_tons;
        if (v == null || Number.isNaN(Number(v))) return sum;
        return sum + Number(v);
      }, 0),
    [emissionsRowsForTotal]
  );

  const section91Grouped = useMemo(
    () => groupSection91Rows(reportData.section91Disclosures || []),
    [reportData.section91Disclosures]
  );

  const toggleSection = (sectionId) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const exportToPDF = async () => {
    setExporting(true);
    try {
      // Create a printable version of the report
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        toastr.error(t('reporting.popupBlocked'));
        return;
      }

      // Get the main content
      const mainContent = document.querySelector('.max-w-7xl');

      if (!mainContent) {
        toastr.error(t('reporting.couldNotFindReportContent'));
        return;
      }

      const pdfEmissionsFiltered = emissionsSummary.filter(
        (item) => !['batch', 'organization_context'].includes(item.questionSet?.category)
      );
      const pdfEmissionsTotal = pdfEmissionsFiltered.reduce((sum, item) => {
        const v = item.answer?.answers?.calculation?.co2e_tons;
        if (v == null || Number.isNaN(Number(v))) return sum;
        return sum + Number(v);
      }, 0);

      // Create HTML for printing
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>${reportData.clientName} - Sustainability Disclosure Report</title>
          <style>
            @page {
              margin: 0;
              size: auto;
              padding-block: 1in;
              padding-inline: 1in;
            }
            body { 
              font-family: Arial, sans-serif; 
              margin: 0; 
              padding: 20px; 
              padding-top: 40px; 
              color: #333;
              line-height: 1.4;
            }
            .header { 
              background: linear-gradient(to right, #059669, #0d9488);
              color: white;
              padding: 30px;
              text-align: center;
              margin-bottom: 30px;
              border-radius: 8px;
            }
            h1 { 
              font-size: 28px; 
              margin-bottom: 20px;
              text-decoration: underline;
              text-decoration-color: rgba(255,255,255,0.3);
            }
            h2 { 
              font-size: 20px; 
              margin: 25px 0 15px 0;
              padding-bottom: 8px;
              border-bottom: 2px solid #059669;
              color: #059669;
            }
            h3 { 
              font-size: 16px; 
              margin: 20px 0 10px 0;
              color: #374151;
            }
            table {
              width: 100%;
              border-collapse: collapse;
              margin: 15px 0;
              font-size: 12px;
            }
            table.status-framework-table {
              table-layout: fixed;
            }
            table.status-framework-table th,
            table.status-framework-table td {
              width: 33.33%;
              word-wrap: break-word;
              vertical-align: top;
            }
            th, td {
              border: 1px solid #e5e7eb;
              padding: 10px;
              text-align: left;
            }
            th {
              background-color: #059669;
              color: white;
              font-weight: 600;
            }
            .compliance-badge {
              display: inline-flex;
              align-items: center;
              padding: 4px 8px;
              border-radius: 12px;
              font-size: 11px;
              font-weight: 600;
            }
            .compliant { background-color: #dcfce7; color: #166534; }
            .non-compliant { background-color: #fee2e2; color: #991b1b; }
            .section {
              margin-top: 30px;
              margin-bottom: 30px;
              page-break-inside: avoid;
            }
            .qa-section {
              border-left: 4px solid #059669;
              padding-left: 15px;
              margin: 15px 0;
              background-color: #f0fdf4;
              padding: 15px;
              border-radius: 0 8px 8px 0;
            }
            .requirements-grid {
              display: grid;
              grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
              gap: 10px;
              margin: 15px 0;
            }
            .requirement-box {
              padding: 10px;
              border-radius: 6px;
              font-size: 11px;
            }
            .ifrs-box { background-color: #dbeafe; border: 1px solid #93c5fd; }
            .disclosure-topic-box { background-color: #ecfeff; border: 1px solid #22d3ee; }
            .audit-box { background-color: #fffbeb; border: 1px solid #fbbf24; }
            .cmf-box { background-color: #dcfce7; border: 1px solid #86efac; }
            .article-box { background-color: #f5f3ff; border: 1px solid #a78bfa; }
            @media print {
              body { margin: 0; padding: 10px; }
              .header { margin: 0 0 20px 0; }
              .section { page-break-inside: avoid; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>${t('reporting.sustainabilityDisclosureReport')}</h1>
            <h2>${reportData.clientName}</h2>
            <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 10px; max-width: 600px; margin: 20px auto; text-align: left; font-size: 14px;">
              <div><strong>${t('reporting.sasbSector')}:</strong> ${reportData.sasbSector}</div>
              <div><strong>${t('reporting.sasbIndustry')}:</strong> ${reportData.sasbIndustry}</div>
              <div><strong>${t('reporting.reportingPeriod')}:</strong> ${reportData.reportingPeriod}</div>
              <div><strong>${t('reporting.reportDate')}:</strong> ${reportData.reportDate}</div>
            </div>
          </div>

          <div class="section">
            <h2>${t('reporting.section11ExecutiveSummary')}</h2>
            <p>
              <strong>${reportData.clientName}</strong> ${t('reporting.executiveSummaryText')}
              <strong>${reportData.finalizedYear}</strong>, ${t('reporting.executiveSummaryCommitment')}
              <strong>${t('reporting.chileanCmfNcg461')}</strong> ${t('reporting.executiveSummaryFrameworks')}
              <strong>${t('reporting.ifrsS1S2')}</strong>, <strong>${t('reporting.sasb')}</strong>, and <strong>${t('reporting.gri')}</strong> ${t('reporting.standards')}.
            </p>
          </div>

          <div class="section">
            <h2>${t('reporting.section12FrameworkDisclosures')}</h2>
            <table class="status-framework-table">
              <thead>
                <tr>
                  <th>${t('answers.framework')}</th>
                  <th>${t('reporting.numberOfDisclosures')}</th>
                  <th>${t('reporting.status')}</th>
                </tr>
              </thead>
              <tbody>
                ${(reportData.disclosureFrameworkSummary || []).filter(c => c.show).map(row => `
                  <tr>
                    <td>${row.framework}</td>
                    <td>${row.numberOfDisclosures}</td>
                    <td>${t('reporting.percentComplete', { percent: row.statusPercent ?? 0 })}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
          </div>

          <div class="section">
            <h2 style="margin-top: 40px;">${t('reporting.sectionEmissions')}</h2>
            ${emissionsSummary.length === 0
              ? `<p class="text-gray-600 italic">${t('reporting.noEmissionsData')}</p>`
              : pdfEmissionsFiltered
                  .map((item) => {
                  const { questionSet, answer } = item;
                  const title = formatEmissionsCategory(questionSet.category);
                  const answersObj = answer?.answers || {};
                  const co2eTons = answersObj.calculation?.co2e_tons;
                  const hasCo2e = co2eTons != null && !Number.isNaN(Number(co2eTons));
                  const noAnswersText = t('reporting.noAnswers');
                  return `
                    <div style="margin: 15px 0; padding: 12px; background: #f0fdf4; border-left: 4px solid #059669; border-radius: 0 8px 8px 0;">
                      <h3>${title}</h3>
                      <p>${hasCo2e ? `<strong>${t('reporting.calculatedEmissions')}:</strong> ${Number(co2eTons).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} tCO₂e` : `<span class="italic">${noAnswersText}</span>`}</p>
                    </div>`;
                }).join('') +
                `<div style="margin-top: 20px; padding: 14px; border: 2px solid #059669; border-radius: 8px; background: #ecfdf5;">
                  <strong>${t('reporting.totalEmissions')}:</strong>
                  ${pdfEmissionsTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} tCO₂e
                </div>`}
          </div>

          <div class="section">
            <h2 style="margin-top: 40px;">${t('reporting.section2CompanyContext')}</h2>
            <h3>${t('reporting.section21SectorOverview')}</h3>
            <p>
              ${t('reporting.sectorOverviewText1')}
            </p>
            <p>
              ${t('reporting.sectorOverviewText2')}
            </p>
          </div>

          ${
            (reportData.section91Disclosures || []).length
              ? `<div class="section">
            <h2 style="margin-top: 40px;">${pdfEscapeText(t('reporting.section91Title'))}</h2>
            ${groupSection91Rows(reportData.section91Disclosures || [])
              .map(
                (dim, dimIdx) => `
            <h3 style="margin-top: 22px; font-size: 15px; color: #0f766e; border-bottom: 2px solid #99f6e4; padding-bottom: 6px;">9.1.${dimIdx + 1} ${pdfEscapeText(t('reporting.sustainabilityDimension'))}: ${pdfEscapeText(dim.dimensionName)}</h3>
            <p style="font-size: 12px; color: #4b5563; margin: 8px 0 14px 0;"><em>${pdfEscapeText(t('reporting.section91DimensionDefinition', { dimension: dim.dimensionName }))}</em></p>
            ${dim.items
              .map(
                (row) => {
                  const req = row.completedRequirements;
                  const reqHtml = req
                    ? `
                <div style="margin-bottom: 14px;">
                  <p><strong>${pdfEscapeText(t('reporting.completedRequirements'))}:</strong></p>
                  <div class="requirements-grid">
                    <div class="requirement-box ifrs-box">
                      <strong>${pdfEscapeText(t('reporting.sustainabilityDimension'))}:</strong><br>
                      <span style="font-size:12px;color:#1d4ed8;font-weight:500;">${pdfEscapeText(dim.dimensionName)}</span>
                    </div>
                    <div class="requirement-box disclosure-topic-box">
                      <strong>${pdfEscapeText(t('reporting.disclosureTopicLabel'))}:</strong><br>
                      <span style="font-size:11px;font-weight:600;color:#155e75;white-space:normal;word-wrap:break-word;overflow-wrap:anywhere;line-height:1.35;display:block;">${pdfEscapeText(req.disclosureFieldTitle || '—')}</span>
                    </div>
                    <div class="requirement-box audit-box">
                      <strong>${pdfEscapeText(t('reporting.sasbAuditCodeLabel'))}:</strong><br>${pdfEscapeText(req.sasbAuditCode)}
                    </div>
                    <div class="requirement-box" style="background:#eef2ff;border-color:#c7d2fe;">
                      <strong>${pdfEscapeText(t('reporting.section91ColReportingPeriod'))}:</strong><br>
                      ${pdfEscapeText(row.reportingPeriod)}
                    </div>
                    <div class="requirement-box article-box">
                      <strong>${pdfEscapeText(t('reporting.cmfNcg30461Article'))}:</strong><br>
                      <span style="white-space:normal;word-wrap:break-word;overflow-wrap:anywhere;line-height:1.35;display:block;">${pdfEscapeText(req.ncg30461Article)}</span>
                    </div>
                    <div class="requirement-box article-box">
                      <strong>${pdfEscapeText(t('reporting.cmfNcg30519Article'))}:</strong><br>
                      <span style="white-space:normal;word-wrap:break-word;overflow-wrap:anywhere;line-height:1.35;display:block;">${pdfEscapeText(req.ncg30519Article)}</span>
                    </div>
                  </div>
                  <div style="margin-top:10px;display:grid;grid-template-columns:minmax(0,1fr) minmax(0,1.15fr) minmax(0,0.55fr);gap:10px;align-items:stretch;">
                    <div class="requirement-box" style="background:#f0fdfa;border-color:#99f6e4;min-width:0;display:flex;flex-direction:column;">
                      <strong>${pdfEscapeText(t('reporting.section91ColSasbMetric'))}:</strong><br>
                      <span style="font-size:11px;flex:1;">${pdfEscapeText(row.sasbMetricName)}</span>
                    </div>
                    <div style="padding:10px 12px;background:#f0fdf4;border:1px solid #86efac;border-radius:6px;min-width:0;display:flex;flex-direction:column;">
                      <strong style="color:#166534;">${pdfEscapeText(t('reporting.cmfRequirements'))}:</strong>
                      <p style="margin:6px 0 0 0;font-size:11px;line-height:1.45;color:#14532d;white-space:pre-wrap;flex:1;">${pdfEscapeText(req.cmfRequirements)}</p>
                    </div>
                    <div class="requirement-box" style="background:#f0f9ff;border-color:#bae6fd;min-width:0;display:flex;flex-direction:column;">
                      <strong>${pdfEscapeText(t('reporting.section91ColValueUnits'))}:</strong><br>
                      <span style="flex:1;">${pdfEscapeText(row.valueUnits)}</span>
                    </div>
                  </div>
                </div>`
                    : '';
                  const hasPdfAns = section91RowHasAnswer(row);
                  const answerBlock = hasPdfAns
                    ? `<div style="margin: 12px 0; padding: 12px; background: #ecfdf5; border-left: 4px solid #16a34a; border-radius: 0 8px 8px 0;">
                    <p style="margin: 0 0 6px 0; font-size: 12px;"><strong>${pdfEscapeText(t('reporting.answer'))}:</strong></p>
                    <p style="margin: 0; font-size: 12px; white-space: pre-wrap;">${pdfEscapeText(formatFillInBlanksAnswerDisplay(row.answer))}</p>
                  </div>`
                    : `<div style="margin: 12px 0; padding: 12px; background: #fffbeb; border-left: 4px solid #d97706; border-radius: 0 8px 8px 0;">
                    <p style="margin: 0 0 6px 0; font-size: 12px;"><strong>${pdfEscapeText(t('reporting.section91ColNoAnswerReason'))}:</strong></p>
                    <p style="margin: 0; font-size: 12px; white-space: pre-wrap;">${pdfEscapeText(row.noAnswerReason)}</p>
                  </div>`;
                  return `
            <div style="margin: 16px 0; border: 1px solid #e5e7eb; border-radius: 10px; overflow: hidden; background: #fafafa;">
              ${reqHtml}
              <div style="padding: 12px 14px; background: #fff; border-bottom: 1px solid #f3f4f6;">
                <p style="margin: 0; font-size: 12px; line-height: 1.5; color: #111827;"><strong>${pdfEscapeText(t('reporting.question'))}:</strong> ${pdfEscapeText(row.engagementQuestion)}</p>
              </div>
              ${answerBlock}
            </div>`;
                }
              )
              .join('')}
                `
              )
              .join('')}
          </div>`
              : ''
          }

          ${reportData.detailedDisclosures.map((dimensionData, dimIndex) => `
            <div class="section">
              <h2 style="margin-top: 40px;">3.${dimIndex + 1} ${t('reporting.sustainabilityDimension')}: ${dimensionData.dimensionName}</h2>
              <p><em>${dimensionData.definition}</em></p>
              
              ${dimensionData.disclosureTopics.map((topicData, topicIndex) => {
                const req = topicData.completedRequirements;
                const reqHtml = req ? `
                <div style="margin-bottom: 18px;">
                  <p><strong>${t('reporting.completedRequirements')}:</strong></p>
                  <div class="requirements-grid">
                    <div class="requirement-box ifrs-box">
                      <strong>${t('reporting.sustainabilityDimension')}:</strong><br>
                      <span style="font-size:12px;color:#1d4ed8;font-weight:500;">${pdfEscapeText(dimensionData.dimensionName)}</span>
                    </div>
                    <div class="requirement-box disclosure-topic-box">
                      <strong>${t('reporting.disclosureTopicLabel')}:</strong><br>
                      <span style="font-size:11px;font-weight:600;color:#155e75;white-space:normal;word-wrap:break-word;overflow-wrap:anywhere;line-height:1.35;display:block;">${pdfEscapeText(req.disclosureFieldTitle || '—')}</span>
                    </div>
                    <div class="requirement-box audit-box">
                      <strong>${t('reporting.sasbAuditCodeLabel')}:</strong><br>${pdfEscapeText(req.sasbAuditCode)}
                    </div>
                    <div class="requirement-box cmf-box">
                      <strong>${t('reporting.cmfRequirements')}:</strong><br>${pdfEscapeText(req.cmfRequirements)}
                    </div>
                    <div class="requirement-box article-box">
                      <strong>${t('reporting.cmfNcg30461Article')}:</strong><br>
                      <span style="white-space:normal;word-wrap:break-word;overflow-wrap:anywhere;line-height:1.35;display:block;">${pdfEscapeText(req.ncg30461Article)}</span>
                    </div>
                    <div class="requirement-box article-box">
                      <strong>${t('reporting.cmfNcg30519Article')}:</strong><br>
                      <span style="white-space:normal;word-wrap:break-word;overflow-wrap:anywhere;line-height:1.35;display:block;">${pdfEscapeText(req.ncg30519Article)}</span>
                    </div>
                  </div>
                </div>` : '';
                return `
                <div style="margin: 20px 0;">
                  ${reqHtml}
                  ${topicData.questionsAndAnswers.map((qa, qaIndex) => `
                    <div class="qa-section">
                      <p><strong>${t('reporting.question')} ${qaIndex + 1}:</strong> ${qa.question}</p>
                      <p><strong>${t('reporting.answer')}:</strong> ${qa.answer}</p>
                    </div>
                  `).join('')}
                </div>
              `;
              }).join('')}
            </div>
          `).join('')}

          <div class="section">
            <h2 style="margin-top: 40px;">${t('reporting.section51Assurance')}</h2>
            <h3>${t('reporting.section51EcominaValidation')}</h3>
            <p>
              ${t('reporting.assuranceDescription')}
            </p>
            <ul>
              <li>${t('reporting.automatedDataChecks')}</li>
              <li>${t('reporting.crossVerification')}</li>
              <li>${t('reporting.validationBenchmarks')}</li>
              <li>${t('reporting.integrationDatabases')}</li>
              <li>${t('reporting.apiConnections')}</li>
            </ul>
          </div>

          <div class="section">
            <h2 style="margin-top: 40px;">${t('reporting.section52SupportingDocuments')}</h2>
            <ul>
              <li>${t('reporting.humanRightsPolicy')}</li>
              <li>${t('reporting.socialResponsibilityPolicy')}</li>
              <li>${t('reporting.dataPrivacyPolicy')}</li>
              <li>${t('reporting.laborCompliancePolicy')}</li>
              <li>${t('reporting.safetyPerformanceReport')}</li>
              <li>${t('reporting.supplierSustainabilityAssessment')}</li>
              <li>${t('reporting.antiCorruptionPolicy')}</li>
              <li>${t('reporting.criticalIncidentRiskManagement')}</li>
              <li>${t('reporting.ghgEmissionsReport')}</li>
              <li>${t('reporting.waterManagementPlan')}</li>
              <li>${t('reporting.independentAssuranceStatement')}</li>
            </ul>
          </div>

          <div style="margin-top: 50px; padding-top: 20px; border-top: 1px solid #ccc; text-align: center; font-size: 11px; color: #666;">
            <p>${t('reporting.generatedByEcomina')} ${new Date().toLocaleDateString()}</p>
            <p>${t('reporting.confidentialInternalUse')}</p>
          </div>
        </body>
        </html>
      `;

      printWindow.document.write(htmlContent);
      printWindow.document.close();

      // Wait for content to load then trigger print
      setTimeout(() => {
        printWindow.print();
        toastr.success(t('reporting.pdfDownloadInitiated'));
      }, 500);

    } catch (error) {
      console.error("Error exporting PDF:", error);
      toastr.error(t('reporting.failedToExportPDF'));
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      toastr.success(t('reporting.excelExportedSuccessfully'));
    } catch (error) {
      console.error("Error exporting Excel:", error);
      toastr.error(t('reporting.failedToExportExcel'));
    } finally {
      setExporting(false);
    }
  };

  const exportToCSV = async () => {
    setExporting(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 1500));
      toastr.success(t('reporting.csvExportedSuccessfully'));
    } catch (error) {
      console.error("Error exporting CSV:", error);
      toastr.error(t('reporting.failedToExportCSV'));
    } finally {
      setExporting(false);
    }
  };

  // Table Styles matching the reference UI
  const tableHeaderStyle = {
    padding: '12px',
    textAlign: 'left',
    border: '1px solid #e5e7eb',
    backgroundColor: '#059669',
    color: 'white',
    fontSize: '14px',
    fontWeight: '600'
  };

  const tableCellStyle = {
    padding: '12px',
    textAlign: 'left',
    border: '1px solid #e5e7eb',
    fontSize: '14px'
  };

  const statusFrameworkColStyle = { width: '33.333%', boxSizing: 'border-box' };

  return (
    <div className="min-h-screen max-sm:w-[100vw] bg-gray-50 font-sans antialiased">
      {/* Action Bar - Matching reference UI */}
      <div className="max-w-7xl mx-auto px-3 sm:px-4 py-4">
        <div className="bg-white rounded-lg shadow border border-gray-100 p-3 mb-4">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-3">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                onClick={() => navigate('/reporting')}
                className="px-3 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 flex items-center gap-2 text-sm"
              >
                <FaChevronDown className="w-3 h-3 rotate-90" />
{t('reporting.back')}
              </button>

              {/* Customer Filter - Only for Super Admin */}
              {localStorage.getItem('role') === 'superAdmin' && (
                <div className="min-w-[200px]">
                  <select
                    className="w-full px-2 py-1.5 bg-white border border-gray-200 rounded-lg focus:border-green-500 text-sm"
                    value={selectedCustomer}
                    onChange={(e) => {
                      setSelectedCustomer(e.target.value);
                      // Update URL without page reload
                      const params = new URLSearchParams(location.search);
                      if (e.target.value) {
                        params.set('customer', e.target.value);
                      } else {
                        params.delete('customer');
                      }
                      navigate(`/detailed-report?${params.toString()}`, { replace: true });
                    }}
                  >
                    <option value="">--Select Customer--</option>
                    {companyAdmins.map((admin) => (
                      <option key={admin.id} value={admin.id}>
                        {admin.companyName || admin.name}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              <div className="relative group">
                <button
                  onClick={exportToPDF}
                  disabled={exporting}
                  className="px-3 py-2 bg-green-500 text-white rounded hover:bg-green-600 flex items-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {exporting ? (
                    <Spinner animation="border" size="sm" className="mr-2" />
                  ) : (
                    <FaDownload className="w-3 h-3" />
                  )}
{exporting ? t('reporting.exporting') : t('reporting.exportToPdf')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <Spinner animation="border" variant="success" style={{ width: '3rem', height: '3rem' }} />
            <p className="mt-4 text-gray-600 text-lg">{t('reporting.loadingReportData')}</p>
          </div>
        ) : localStorage.getItem('role') === 'superAdmin' && !selectedCustomer ? (
          <div className="flex flex-col items-center justify-center py-20">
            <FaUser className="w-16 h-16 text-gray-400 mb-4" />
            <p className="text-gray-600 text-lg font-semibold">Please select a customer to view the detailed report</p>
            <p className="text-gray-500 text-sm mt-2">Choose a customer from the dropdown above to generate their report</p>
          </div>
        ) : (
          /* Main Content */
          <div className="space-y-6">
            {/* ----------------------------------------------------
            1. Cover/Intro Page Details
          ---------------------------------------------------- */}
            <section className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
              <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-center py-8 px-4">
                <h1 className="text-2xl sm:text-3xl font-bold mb-4 underline decoration-white/30">{t('reporting.sustainabilityDisclosureReport')}</h1>
                <div className="mt-6 space-y-2">
                  <h2 className="text-xl sm:text-2xl font-semibold">{reportData.clientName}</h2>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl mx-auto text-sm">
                    <p><strong>{t('reporting.sasbSector')}:</strong> {reportData.sasbSector}</p>
                    <p><strong>{t('reporting.sasbIndustry')}:</strong> {reportData.sasbIndustry}</p>
                    <p><strong>{t('reporting.reportingPeriod')}:</strong> {reportData.reportingPeriod}</p>
                    <p><strong>{t('reporting.reportDate')}:</strong> {reportData.reportDate}</p>
                  </div>
                </div>
              </div>
            </section>

            {/* ----------------------------------------------------
            1.1 Executive Summary (Re-labeled from 2)
          ---------------------------------------------------- */}
            <section className="bg-white rounded-lg shadow border border-gray-100 p-6">
              <h2 className="text-lg font-semibold text-gray-800 border-b border-gray-200 pb-3 mb-4 flex items-center">
                <FaFileAlt className="w-4 h-4 mr-2 text-green-600" />
{t('reporting.section11ExecutiveSummary')}
              </h2>
              <div className="prose prose-sm max-w-none">
                <p className="text-gray-700 leading-relaxed">
                  <strong>{reportData.clientName}</strong> {t('reporting.executiveSummaryText')}{' '}
                  <strong>{reportData.finalizedYear}</strong>, {t('reporting.executiveSummaryCommitment')}{' '}
                  <strong>{t('reporting.chileanCmfNcg461')}</strong> {t('reporting.executiveSummaryFrameworks')}{' '}
                  <strong>{t('reporting.ifrsS1S2')}</strong>, <strong>{t('reporting.sasb')}</strong>, and <strong>{t('reporting.gri')}</strong> {t('reporting.standards')}.
                </p>
              </div>
            </section>

            {/* ----------------------------------------------------
            1.2 Status
          ---------------------------------------------------- */}
            <section className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                onClick={() => toggleSection('compliance')}
              >
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FaChartBar className="w-4 h-4 mr-2 text-green-600" />
{t('reporting.section12FrameworkDisclosures')}
                </h2>
                <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                  {expandedSections['compliance'] ? (
                    <FaChevronUp className="w-2.5 h-2.5 text-gray-600" />
                  ) : (
                    <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                  )}
                </div>
              </div>

              {expandedSections['compliance'] && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  <div className="overflow-x-auto">
                    <table className="w-full border-collapse table-fixed">
                      <thead>
                        <tr>
                          <th style={{ ...tableHeaderStyle, ...statusFrameworkColStyle }}>{t('answers.framework')}</th>
                          <th style={{ ...tableHeaderStyle, ...statusFrameworkColStyle }}>{t('reporting.numberOfDisclosures')}</th>
                          <th style={{ ...tableHeaderStyle, ...statusFrameworkColStyle }}>{t('reporting.status')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(reportData.disclosureFrameworkSummary || []).filter(c => c.show).map((row, index) => (
                          <tr key={index} className="border-b border-gray-200 hover:bg-gray-50">
                            <td style={{ ...tableCellStyle, ...statusFrameworkColStyle }} className="font-medium align-top">{row.framework}</td>
                            <td style={{ ...tableCellStyle, ...statusFrameworkColStyle }} className="align-top">{row.numberOfDisclosures}</td>
                            <td style={{ ...tableCellStyle, ...statusFrameworkColStyle }} className="align-top">
                              {t('reporting.percentComplete', { percent: row.statusPercent ?? 0 })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </section>

            {/* ----------------------------------------------------
            2.5 Emissions / GHG (from /emissions-questions)
          ---------------------------------------------------- */}
            <section className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                onClick={() => toggleSection('emissions')}
              >
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FaLeaf className="w-4 h-4 mr-2 text-green-600" />
                  {t('reporting.sectionEmissions')}
                </h2>
                <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                  {expandedSections['emissions'] ? (
                    <FaChevronUp className="w-2.5 h-2.5 text-gray-600" />
                  ) : (
                    <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                  )}
                </div>
              </div>

              {expandedSections['emissions'] && (
                <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4">
                  {emissionsSummary.length === 0 ? (
                    <p className="text-gray-600 italic">
                      {t('reporting.noEmissionsData')}
                    </p>
                  ) : (
                    <>
                      {emissionsRowsForTotal.map((item, idx) => {
                      const { questionSet, answer } = item;
                      const title = formatEmissionsCategory(questionSet.category);
                      const answersObj = answer?.answers || {};
                      const co2eTons = answersObj.calculation?.co2e_tons;

                      return (
                        <div key={idx} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                          <div className="bg-gradient-to-r from-emerald-50 to-teal-50 p-3 border-b border-gray-200 flex flex-wrap items-center justify-between gap-2">
                            <h3 className="text-base font-semibold text-gray-800">
                              {title}
                            </h3>
                            {co2eTons != null && !Number.isNaN(Number(co2eTons)) ? (
                              <div className="flex items-center gap-2">
                                <FaChartBar className="w-4 h-4 text-green-600 flex-shrink-0" />
                                <span className="font-semibold text-green-800">
                                  {Number(co2eTons).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} tCO₂e
                                </span>
                              </div>
                            ) : (
                              <p className="text-gray-500 italic text-sm">
                                {t('reporting.noAnswers')}
                              </p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                      <div className="bg-white rounded-lg border-2 border-emerald-600 p-4 flex flex-wrap items-center justify-between gap-2">
                        <span className="text-base font-semibold text-gray-800">{t('reporting.totalEmissions')}</span>
                        <span className="text-lg font-bold text-emerald-800">
                          {totalEmissionsTcO2e.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })} tCO₂e
                        </span>
                      </div>
                    </>
                  )}
                </div>
              )}
            </section>

            {/* ----------------------------------------------------
            2. Company and Industry Context (NEW SECTION)
          ---------------------------------------------------- */}
            <section className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                onClick={() => toggleSection('context')}
              >
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FaIndustry className="w-4 h-4 mr-2 text-green-600" />
                  {t('reporting.section2CompanyContext')}
                </h2>
                <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                  {expandedSections['context'] ? (
                    <FaChevronUp className="w-2.5 h-2.5 text-gray-600" />
                  ) : (
                    <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                  )}
                </div>
              </div>

              {expandedSections['context'] && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  <h3 className="text-base font-semibold text-gray-800 mb-3">
                    {t('reporting.section21SectorOverview')}
                  </h3>
                  <div className="prose prose-sm max-w-none text-gray-700 leading-relaxed">
                    <p>
                      {t('reporting.sectorOverviewText1')}
                    </p>
                    <p>
                      {t('reporting.sectorOverviewText2')}
                    </p>
                  </div>
                </div>
              )}
            </section>

            {(reportData.section91Disclosures || []).length > 0 && (
              <section className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
                <div
                  className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                  onClick={() => toggleSection('section91')}
                >
                  <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                    <FaTag className="w-4 h-4 mr-2 text-teal-600" />
                    {t('reporting.section91Title')}
                  </h2>
                  <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                    {expandedSections['section91'] ? (
                      <FaChevronUp className="w-2.5 h-2.5 text-gray-600" />
                    ) : (
                      <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                    )}
                  </div>
                </div>

                {expandedSections['section91'] && (
                  <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-6">
                    {section91Grouped.map((dimensionData, dimIndex) => (
                      <div key={`section91-dim-${dimIndex}-${dimensionData.dimensionName}`} className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
                        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 border-b border-gray-200">
                          <h3 className="text-lg font-semibold text-gray-800 mb-2">
                            {`9.1.${dimIndex + 1} ${t('reporting.sustainabilityDimension')}: ${dimensionData.dimensionName}`}
                          </h3>
                          <p className="text-gray-600 text-sm italic">
                            {t('reporting.section91DimensionDefinition', { dimension: dimensionData.dimensionName })}
                          </p>
                        </div>

                        <div className="p-4 space-y-6">
                          {dimensionData.items.map((row) => {
                            const req = row.completedRequirements;
                            const hasAns = section91RowHasAnswer(row);
                            const reasonDisplay = (() => {
                              const r = row.noAnswerReason;
                              if (r == null) return '—';
                              const s = String(r).trim();
                              if (s === '' || s === '—') return '—';
                              return s;
                            })();
                            return (
                              <div
                                key={row.rowKey}
                                className="border border-gray-200 rounded-xl overflow-hidden bg-gray-50/40"
                              >
                                {req && (
                                  <div className="p-4 border-b border-gray-200 bg-white">
                                    <p className="font-semibold text-gray-800 mb-3">{t('reporting.completedRequirements')}:</p>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3 text-sm">
                                      <div className="bg-blue-50 p-2 rounded border border-blue-200 min-w-0">
                                        <p className="font-semibold text-blue-800">{t('reporting.sustainabilityDimension')}:</p>
                                        <p className="text-blue-700 font-medium break-words [overflow-wrap:anywhere]">
                                          {dimensionData.dimensionName}
                                        </p>
                                      </div>
                                      <div className="bg-cyan-50 p-2 rounded border border-cyan-200 min-w-0">
                                        <p className="font-semibold text-cyan-900">{t('reporting.disclosureTopicLabel')}:</p>
                                        <p className="text-xs font-semibold text-cyan-900 mt-0.5 break-words [overflow-wrap:anywhere] leading-snug whitespace-normal">
                                          {req.disclosureFieldTitle || '—'}
                                        </p>
                                      </div>
                                      <div className="bg-amber-50 p-2 rounded border border-amber-200 min-w-0">
                                        <p className="font-semibold text-amber-900">{t('reporting.sasbAuditCodeLabel')}:</p>
                                        <p className="text-amber-800 break-words [overflow-wrap:anywhere]">{req.sasbAuditCode}</p>
                                      </div>
                                      <div className="bg-indigo-50 p-2 rounded border border-indigo-200 min-w-0">
                                        <p className="font-semibold text-indigo-900">{t('reporting.section91ColReportingPeriod')}:</p>
                                        <p className="text-indigo-950 break-words [overflow-wrap:anywhere] leading-snug">
                                          {row.reportingPeriod}
                                        </p>
                                      </div>
                                      <div className="bg-violet-50 p-2 rounded border border-violet-200 min-w-0">
                                        <p className="font-semibold text-violet-900">{t('reporting.cmfNcg30461Article')}</p>
                                        <p className="text-violet-800 mt-0.5 break-words [overflow-wrap:anywhere] leading-snug whitespace-pre-wrap">
                                          {req.ncg30461Article}
                                        </p>
                                      </div>
                                      <div className="bg-purple-50 p-2 rounded border border-purple-200 min-w-0">
                                        <p className="font-semibold text-purple-900">{t('reporting.cmfNcg30519Article')}</p>
                                        <p className="text-purple-900 mt-0.5 break-words [overflow-wrap:anywhere] leading-snug whitespace-pre-wrap">
                                          {req.ncg30519Article}
                                        </p>
                                      </div>
                                    </div>
                                    <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-3 text-sm items-stretch">
                                      <div className="bg-teal-50 p-3 rounded-lg border border-teal-200 min-w-0 flex flex-col">
                                        <p className="font-semibold text-teal-900 mb-1">{t('reporting.section91ColSasbMetric')}</p>
                                        <p className="text-teal-950 break-words [overflow-wrap:anywhere] leading-snug whitespace-pre-wrap grow">
                                          {row.sasbMetricName}
                                        </p>
                                      </div>
                                      <div className="bg-green-50 p-3 rounded-lg border border-green-200 min-w-0 flex flex-col">
                                        <p className="font-semibold text-green-800 mb-1">{t('reporting.cmfRequirements')}</p>
                                        <p className="text-green-800 break-words [overflow-wrap:anywhere] whitespace-pre-wrap leading-relaxed grow">
                                          {req.cmfRequirements}
                                        </p>
                                      </div>
                                      <div className="bg-sky-50 p-3 rounded-lg border border-sky-200 min-w-0 flex flex-col">
                                        <p className="font-semibold text-sky-900 mb-1">{t('reporting.section91ColValueUnits')}</p>
                                        <p className="text-sky-950 break-words [overflow-wrap:anywhere] grow">
                                          {row.valueUnits}
                                        </p>
                                      </div>
                                    </div>
                                  </div>
                                )}

                                <div className="p-4 border-b border-gray-100 bg-white">
                                  <p className="font-semibold text-gray-800 mb-2 flex items-start">
                                    <FaQuestionCircle className="w-4 h-4 mr-2 text-teal-600 mt-0.5 flex-shrink-0" />
                                    {t('reporting.question')}
                                  </p>
                                  <p className="text-gray-800 leading-relaxed whitespace-pre-wrap pl-6 md:pl-7">
                                    {row.engagementQuestion}
                                  </p>
                                </div>

                                <div className="p-4 border-b border-gray-100 bg-white space-y-4">
                                  {hasAns ? (
                                    <div className="border-l-4 border-green-500 pl-4 bg-green-50/60 rounded-r-lg py-3 pr-3">
                                      <p className="font-semibold text-gray-800 mb-2 flex items-start">
                                        <FaCheckCircle className="w-4 h-4 mr-2 text-green-600 mt-0.5 flex-shrink-0" />
                                        {t('reporting.answer')}
                                      </p>
                                      <p className="text-gray-800 whitespace-pre-wrap ml-6 md:ml-7">
                                        {formatFillInBlanksAnswerDisplay(row.answer)}
                                      </p>
                                    </div>
                                  ) : (
                                    <div className="border-l-4 border-amber-500 pl-4 bg-amber-50/60 rounded-r-lg py-3 pr-3">
                                      <p className="font-semibold text-gray-800 mb-2 flex items-start">
                                        <FaExclamationCircle className="w-4 h-4 mr-2 text-amber-600 mt-0.5 flex-shrink-0" />
                                        {t('reporting.section91ColNoAnswerReason')}
                                      </p>
                                      <p className="text-gray-800 whitespace-pre-wrap ml-6 md:ml-7">
                                        {reasonDisplay}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            )}

            {/* ----------------------------------------------------
            3. Detailed Disclosures by Dimension (Re-labeled from 3. Detailed)
          ---------------------------------------------------- */}
            <section className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                onClick={() => toggleSection('detailed')}
              >
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FaExpandArrowsAlt className="w-4 h-4 mr-2 text-green-600" />
{t('reporting.section3DetailedDisclosures')}
                </h2>
                <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                  {expandedSections['detailed'] ? (
                    <FaChevronUp className="w-2.5 h-2.5 text-gray-600" />
                  ) : (
                    <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                  )}
                </div>
              </div>

              {expandedSections['detailed'] && (
                <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-6">
                  {reportData.detailedDisclosures.map((dimensionData, dimIndex) => (
                    <div key={dimIndex} className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-4 border-b border-gray-200">
                        <h3 className="text-lg font-semibold text-gray-800 mb-2">
                          {/* Updated numbering here as well */}
                          {`3.${dimIndex + 1} ${t('reporting.sustainabilityDimension')}: ${dimensionData.dimensionName}`}
                        </h3>
                        <p className="text-gray-600 text-sm italic">
                          {dimensionData.definition}
                        </p>
                      </div>

                      <div className="p-4 space-y-4">
                        {dimensionData.disclosureTopics.map((topicData, topicIndex) => {
                          const req = topicData.completedRequirements;
                          return (
                          <div key={topicData.topicKey || topicIndex} className="border border-gray-200 rounded-lg overflow-hidden p-4 space-y-4">
                              {req && (
                                <div>
                                  <p className="font-semibold text-gray-800 mb-2">{t('reporting.completedRequirements')}:</p>
                                  <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-6 gap-3 text-sm">
                                    <div className="bg-blue-50 p-2 rounded border border-blue-200 min-w-0">
                                      <p className="font-semibold text-blue-800">{t('reporting.sustainabilityDimension')}:</p>
                                      <p className="text-blue-700 font-medium break-words [overflow-wrap:anywhere]">
                                        {dimensionData.dimensionName}
                                      </p>
                                    </div>
                                    <div className="bg-cyan-50 p-2 rounded border border-cyan-200 min-w-0">
                                      <p className="font-semibold text-cyan-900">{t('reporting.disclosureTopicLabel')}:</p>
                                      <p className="text-xs font-semibold text-cyan-900 mt-0.5 break-words [overflow-wrap:anywhere] leading-snug whitespace-normal">
                                        {req.disclosureFieldTitle || '—'}
                                      </p>
                                    </div>
                                    <div className="bg-amber-50 p-2 rounded border border-amber-200 min-w-0">
                                      <p className="font-semibold text-amber-900">{t('reporting.sasbAuditCodeLabel')}:</p>
                                      <p className="text-amber-800 break-words [overflow-wrap:anywhere]">{req.sasbAuditCode}</p>
                                    </div>
                                    <div className="bg-green-50 p-2 rounded border border-green-200 min-w-0">
                                      <p className="font-semibold text-green-800">{t('reporting.cmfRequirements')}:</p>
                                      <p className="text-green-700 break-words [overflow-wrap:anywhere] whitespace-pre-wrap">{req.cmfRequirements}</p>
                                    </div>
                                    <div className="bg-violet-50 p-2 rounded border border-violet-200 min-w-0">
                                      <p className="font-semibold text-violet-900">{t('reporting.cmfNcg30461Article')}</p>
                                      <p className="text-violet-800 mt-0.5 break-words [overflow-wrap:anywhere] leading-snug whitespace-pre-wrap">
                                        {req.ncg30461Article}
                                      </p>
                                    </div>
                                    <div className="bg-purple-50 p-2 rounded border border-purple-200 min-w-0">
                                      <p className="font-semibold text-purple-900">{t('reporting.cmfNcg30519Article')}</p>
                                      <p className="text-purple-900 mt-0.5 break-words [overflow-wrap:anywhere] leading-snug whitespace-pre-wrap">
                                        {req.ncg30519Article}
                                      </p>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {topicData.questionsAndAnswers.map((qa, qaIndex) => (
                                <div key={qaIndex} className="border-l-4 border-green-500 pl-4 bg-green-50/50 rounded-r-lg">
                                    <p className="font-semibold text-gray-800 mb-2 flex items-start">
                                      <FaQuestionCircle className="w-4 h-4 mr-2 text-green-600 mt-0.5 flex-shrink-0" />
                                      {t('reporting.question')} {qaIndex + 1}: {qa.question}
                                    </p>
                                    <div className="bg-white p-3 rounded border border-gray-200">
                                      <p className="font-semibold text-gray-700 mb-1 flex items-start">
                                        <FaCheckCircle className="w-4 h-4 mr-2 text-green-500 mt-0.5 flex-shrink-0" />
                                        {t('reporting.answer')}:
                                      </p>
                                      <p className="text-gray-700 whitespace-pre-wrap ml-6">{qa.answer}</p>
                                    </div>
                                </div>
                              ))}
                          </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>

            {/* ----------------------------------------------------
            5.1 Assurance and Verification (Re-labeled from 5)
          ---------------------------------------------------- */}
            <section className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                onClick={() => toggleSection('assurance')}
              >
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FaLock className="w-4 h-4 mr-2 text-green-600" />
                  {t('reporting.section51Assurance')}
                </h2>
                <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                  {expandedSections['assurance'] ? (
                    <FaChevronUp className="w-2.5 h-2.5 text-gray-600" />
                  ) : (
                    <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                  )}
                </div>
              </div>

              {expandedSections['assurance'] && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  <h3 className="text-base font-semibold text-gray-800 mb-3">
                    {t('reporting.section51EcominaValidation')}
                  </h3>
                  <p className="text-gray-700 leading-relaxed mb-4">
                    {t('reporting.assuranceDescription')}
                  </p>
                  <ul className="list-disc list-inside space-y-2 text-sm text-gray-700 ml-4">
                    <li className="flex items-start">
                      -<span>{t('reporting.automatedDataChecks')}</span>
                    </li>
                    <li className="flex items-start">
                      -<span>{t('reporting.crossVerification')}</span>
                    </li>
                    <li className="flex items-start">
                      -<span>{t('reporting.validationBenchmarks')}</span>
                    </li>
                    <li className="flex items-start">
                      -<span>{t('reporting.integrationDatabases')}</span>
                    </li>
                    <li className="flex items-start">
                      -<span>{t('reporting.apiConnections')}</span>
                    </li>
                  </ul>
                </div>
              )}
            </section>

            {/* ----------------------------------------------------
            5.2 Supporting Documents (NEW SECTION)
          ---------------------------------------------------- */}
            <section className="bg-white rounded-lg shadow border border-gray-100 overflow-hidden">
              <div
                className="p-4 cursor-pointer hover:bg-gray-50 flex items-center justify-between"
                onClick={() => toggleSection('supportingDocuments')}
              >
                <h2 className="text-lg font-semibold text-gray-800 flex items-center">
                  <FaFile className="w-4 h-4 mr-2 text-green-600" />
                  {t('reporting.section52SupportingDocuments')}
                </h2>
                <div className="w-5 h-5 bg-gray-100 rounded-full flex items-center justify-center">
                  {expandedSections['supportingDocuments'] ? (
                    <FaChevronUp className="w-2.5 h-2.5 text-gray-600" />
                  ) : (
                    <FaChevronDown className="w-2.5 h-2.5 text-gray-600" />
                  )}
                </div>
              </div>

              {expandedSections['supportingDocuments'] && (
                <div className="border-t border-gray-200 p-4 bg-gray-50">
                  <ul className="list-disc list-inside space-y-2 text-sm text-gray-700 ml-4">
                    <li className="flex items-start">
                      <FaFile className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                      <span>{t('reporting.humanRightsPolicy')}</span>
                    </li>
                    <li className="flex items-start">
                      <FaFile className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                      <span>{t('reporting.socialResponsibilityPolicy')}</span>
                    </li>
                    <li className="flex items-start">
                      <FaFile className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                      <span>{t('reporting.dataPrivacyPolicy')}</span>
                    </li>
                    <li className="flex items-start">
                      <FaFile className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                      <span>{t('reporting.laborCompliancePolicy')}</span>
                    </li>
                    <li className="flex items-start">
                      <FaFile className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                      <span>{t('reporting.safetyPerformanceReport')}</span>
                    </li>
                    <li className="flex items-start">
                      <FaFile className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                      <span>{t('reporting.supplierSustainabilityAssessment')}</span>
                    </li>
                    <li className="flex items-start">
                      <FaFile className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                      <span>{t('reporting.antiCorruptionPolicy')}</span>
                    </li>
                    <li className="flex items-start">
                      <FaFile className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                      <span>{t('reporting.criticalIncidentRiskManagement')}</span>
                    </li>
                    <li className="flex items-start">
                      <FaFile className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                      <span>{t('reporting.ghgEmissionsReport')}</span>
                    </li>
                    <li className="flex items-start">
                      <FaFile className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                      <span>{t('reporting.waterManagementPlan')}</span>
                    </li>
                    <li className="flex items-start">
                      <FaFile className="w-4 h-4 mr-2 mt-0.5 text-green-500 flex-shrink-0" />
                      <span>{t('reporting.independentAssuranceStatement')}</span>
                    </li>
                  </ul>
                </div>
              )}
            </section>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReportsPage;